import { InputAdornment } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface MoneyInputProps extends BaseInputProps {
  /** Valor numérico en centavos o unidades enteras */
  value: number | null;
  /** Callback cuando cambia el valor */
  onChange: (value: number | null) => void;
  /** Valor mínimo permitido */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Moneda (default: 'CLP') - CLP, USD, UF */
  currency?: 'CLP' | 'USD' | 'UF';
  /** Nombre del campo para formularios */
  name?: string;
}

/**
 * Formatea un número como dinero según la moneda
 */
const formatMoneyDisplay = (
  value: number | null,
  currency: 'CLP' | 'USD' | 'UF',
): string => {
  if (value === null) return '';

  const formatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: currency === 'UF' ? 2 : 0,
    maximumFractionDigits: currency === 'UF' ? 2 : 0,
  });

  return formatter.format(value);
};

/**
 * Parsea un string formateado a número
 */
const parseMoneyValue = (displayValue: string): number | null => {
  if (!displayValue) return null;

  // Remover separadores de miles y convertir coma decimal a punto
  const cleanValue = displayValue
    .replace(/\./g, '') // Remover puntos (separadores de miles)
    .replace(',', '.'); // Convertir coma decimal a punto

  const num = parseFloat(cleanValue);
  return isNaN(num) ? null : Math.round(num);
};

/**
 * Obtiene el símbolo de moneda
 */
const getCurrencySymbol = (currency: 'CLP' | 'USD' | 'UF'): string => {
  switch (currency) {
    case 'USD':
      return 'US$';
    case 'UF':
      return 'UF';
    case 'CLP':
    default:
      return '$';
  }
};

/**
 * Características:
 * - Formatea visualmente como dinero ($1.000.000)
 * - Procesa input como string para formateo
 * - Devuelve number | null en onChange
 * - Soporta diferentes monedas (CLP, USD, UF)
 */
export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  min,
  max,
  currency = 'CLP',
  name,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(
    formatMoneyDisplay(value, currency),
  );
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;

      // Permitir string vacío
      if (rawValue === '') {
        setDisplayValue('');
        onChange(null);
        return;
      }

      // Solo permitir dígitos y separadores
      const cleanInput = rawValue.replace(/[^\d.,]/g, '');

      // Validar formato
      const numValue = parseMoneyValue(cleanInput);

      if (numValue !== null) {
        // Validar rango
        if (min !== undefined && numValue < min) {
          return;
        }
        if (max !== undefined && numValue > max) {
          return;
        }
      }

      // Formatear el número con separadores mientras se escribe
      if (numValue !== null) {
        const formatted = formatMoneyDisplay(numValue, currency);
        setDisplayValue(formatted);
        onChange(numValue);
      } else {
        // Si no es un número válido, mantener el input limpio
        setDisplayValue(cleanInput);
      }
    },
    [onChange, min, max, currency],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // Mantener el formato al enfocar
    if (value !== null) {
      setDisplayValue(formatMoneyDisplay(value, currency));
    }
  }, [value, currency]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    const numValue = parseMoneyValue(displayValue);

    if (numValue !== null) {
      onChange(numValue);
      setDisplayValue(formatMoneyDisplay(numValue, currency));
    } else {
      setDisplayValue('');
      onChange(null);
    }
  }, [displayValue, onChange, currency]);

  // Sincronizar con valor externo cuando no está en foco
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatMoneyDisplay(value, currency));
    }
  }, [value, currency, isFocused]);

  return (
    <StyledTextField
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      label={label}
      disabled={disabled}
      error={error}
      helperText={helperText}
      required={required}
      fullWidth={fullWidth}
      placeholder={placeholder}
      name={name}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {getCurrencySymbol(currency)}
          </InputAdornment>
        ),
      }}
    />
  );
};
